/**
 * BabyKidsBookingFlow - Multi-step booking for Baby & Kids services
 * Flow: Bundle → Delivery → Installation → Schedule → Pay
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Checkbox } from '../../components/ui/checkbox';
import { Progress } from '../../components/ui/progress';
import { Calendar } from '../../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Package, Truck, Wrench, Calendar as CalendarIcon, CreditCard,
  ChevronRight, ChevronLeft, CheckCircle, MapPin, Clock,
  Shield, AlertTriangle, Phone, Mail, User, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { BabyKidsTrustBadge } from '../../components/services/BabyKidsBadges';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Booking steps
const STEPS = [
  { id: 1, name: 'Bundle', icon: Package, description: 'Select items' },
  { id: 2, name: 'Delivery', icon: Truck, description: 'Delivery options' },
  { id: 3, name: 'Services', icon: Wrench, description: 'Installation & Setup' },
  { id: 4, name: 'Schedule', icon: CalendarIcon, description: 'Pick date & time' },
  { id: 5, name: 'Payment', icon: CreditCard, description: 'Confirm & pay' },
];

// Time slots
const TIME_SLOTS = [
  { id: 'morning', label: 'Morning (9am - 12pm)', value: '09:00-12:00' },
  { id: 'afternoon', label: 'Afternoon (12pm - 4pm)', value: '12:00-16:00' },
  { id: 'evening', label: 'Evening (4pm - 7pm)', value: '16:00-19:00' },
];

// Step Progress Component
function StepProgress({ currentStep, steps }) {
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isActive = currentStep === step.id;
          const isComplete = currentStep > step.id;
          
          return (
            <React.Fragment key={step.id}>
              <div className={`flex flex-col items-center ${isActive ? 'text-indigo-600' : isComplete ? 'text-green-600' : 'text-gray-400'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  isActive ? 'bg-indigo-100 border-2 border-indigo-600' :
                  isComplete ? 'bg-green-100' : 'bg-gray-100'
                }`}>
                  {isComplete ? <CheckCircle className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                </div>
                <span className="text-xs mt-1 font-medium hidden sm:block">{step.name}</span>
              </div>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 ${isComplete ? 'bg-green-500' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// Bundle Selection Step
function BundleStep({ listing, bundleItems, onItemToggle, onNext }) {
  const [selectedItems, setSelectedItems] = useState(
    bundleItems.reduce((acc, item) => ({ ...acc, [item.id]: item.recommended }), {})
  );
  
  const handleToggle = (itemId) => {
    setSelectedItems(prev => {
      const newState = { ...prev, [itemId]: !prev[itemId] };
      onItemToggle(newState);
      return newState;
    });
  };
  
  const selectedCount = Object.values(selectedItems).filter(Boolean).length;
  const totalPrice = bundleItems.filter(item => selectedItems[item.id]).reduce((sum, item) => sum + item.price, 0);
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="w-5 h-5 text-indigo-600" />
          Build Your Bundle
        </CardTitle>
        <CardDescription>
          Select items to include in your order
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Main listing item */}
        <div className="p-4 bg-indigo-50 rounded-lg border-2 border-indigo-200">
          <div className="flex items-center gap-3">
            <Checkbox checked={true} disabled />
            <div className="flex-1">
              <p className="font-medium">{listing?.title || 'Main Service'}</p>
              <p className="text-sm text-gray-500">{listing?.description?.slice(0, 100)}...</p>
            </div>
            <span className="font-bold text-indigo-600">
              {listing?.currency === 'LKR' || (listing?.country || '').toUpperCase() === 'LK' || (listing?.country || '').toUpperCase() === 'SL' ? 'Rs.' : '£'}{listing?.price || 0}
            </span>
          </div>
        </div>
        
        {/* Add-on items */}
        <div className="space-y-2">
          <h4 className="font-medium text-gray-700">Add-on Services</h4>
          {bundleItems.map(item => (
            <div 
              key={item.id}
              className={`p-3 rounded-lg border cursor-pointer transition-all ${
                selectedItems[item.id] ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => handleToggle(item.id)}
            >
              <div className="flex items-center gap-3">
                <Checkbox checked={selectedItems[item.id] || false} />
                <div className="flex-1">
                  <p className="font-medium text-sm">{item.name}</p>
                  {item.recommended && (
                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700">Recommended</Badge>
                  )}
                </div>
                <span className="font-medium">
                  {listing?.currency === 'LKR' || (listing?.country || '').toUpperCase() === 'LK' || (listing?.country || '').toUpperCase() === 'SL' ? 'Rs.' : '£'}{item.price}
                </span>
              </div>
            </div>
          ))}
        </div>
        
        {/* Summary */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">{selectedCount} add-ons selected</span>
            <span className="text-lg font-bold">
              Total: {listing?.currency === 'LKR' || (listing?.country || '').toUpperCase() === 'LK' || (listing?.country || '').toUpperCase() === 'SL' ? 'Rs.' : '£'}{(listing?.price || 0) + totalPrice}
            </span>
          </div>
        </div>
        
        <Button className="w-full" onClick={() => onNext(selectedItems)}>
          Continue to Delivery
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
}

// Delivery Options Step
function DeliveryStep({ listing, deliveryOption, onOptionChange, onNext, onBack }) {
  const [option, setOption] = useState(deliveryOption || 'standard');
  const [address, setAddress] = useState('');
  const [postcode, setPostcode] = useState('');
  const [phone, setPhone] = useState('');
  
  const deliveryOptions = [
    { id: 'collection', label: 'Collection', description: 'Collect from provider', price: 0 },
    { id: 'standard', label: 'Standard Delivery', description: '3-5 working days', price: listing?.currency === 'LKR' ? 500 : 5.99 },
    { id: 'express', label: 'Express Delivery', description: 'Next working day', price: listing?.currency === 'LKR' ? 1500 : 12.99 },
  ];
  
  const selectedDelivery = deliveryOptions.find(o => o.id === option);
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Truck className="w-5 h-5 text-indigo-600" />
          Delivery Options
        </CardTitle>
        <CardDescription>
          Choose how you want to receive your items
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <RadioGroup value={option} onValueChange={setOption}>
          {deliveryOptions.map(opt => (
            <label 
              key={opt.id}
              className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer ${
                option === opt.id ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'
              }`}
            >
              <div className="flex items-center gap-3">
                <RadioGroupItem value={opt.id} />
                <div>
                  <p className="font-medium">{opt.label}</p>
                  <p className="text-sm text-gray-500">{opt.description}</p>
                </div>
              </div>
              <span className="font-medium">
                {opt.price === 0 ? 'Free' : `${listing?.currency === 'LKR' || (listing?.country || '').toUpperCase() === 'LK' || (listing?.country || '').toUpperCase() === 'SL' ? 'Rs.' : '£'}${opt.price}`}
              </span>
            </label>
          ))}
        </RadioGroup>
        
        {option !== 'collection' && (
          <div className="space-y-4 pt-4 border-t">
            <h4 className="font-medium">Delivery Address</h4>
            <div>
              <Label>Address</Label>
              <Textarea 
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Enter your full address"
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Postcode</Label>
                <Input 
                  value={postcode}
                  onChange={(e) => setPostcode(e.target.value)}
                  placeholder="SW1A 1AA"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input 
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+44 7XXX XXX XXX"
                  className="mt-1"
                />
              </div>
            </div>
          </div>
        )}
        
        <div className="flex gap-3 pt-4">
          <Button variant="outline" onClick={onBack}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button 
            className="flex-1" 
            onClick={() => onNext({ option, address, postcode, phone, price: selectedDelivery?.price || 0 })}
            disabled={option !== 'collection' && (!address || !postcode || !phone)}
          >
            Continue to Services
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Installation Services Step
function ServicesStep({ listing, services, onServicesChange, onNext, onBack }) {
  const [selectedServices, setSelectedServices] = useState({});
  
  const installationServices = [
    { id: 'assembly', name: 'Assembly & Setup', description: 'Professional assembly of items', price: listing?.currency === 'LKR' ? 2000 : 25, recommended: listing?.subcategory?.includes('furniture') },
    { id: 'installation', name: 'Installation Check', description: 'Safety check and guidance (info-only)', price: listing?.currency === 'LKR' ? 3000 : 35, safety: true },
    { id: 'demo', name: 'Product Demo', description: '15-min demonstration of features', price: listing?.currency === 'LKR' ? 1000 : 15 },
    { id: 'disposal', name: 'Packaging Disposal', description: 'We take away all packaging', price: listing?.currency === 'LKR' ? 500 : 5 },
  ];
  
  const handleToggle = (serviceId) => {
    setSelectedServices(prev => {
      const newState = { ...prev, [serviceId]: !prev[serviceId] };
      onServicesChange(newState);
      return newState;
    });
  };
  
  const totalServicePrice = installationServices
    .filter(s => selectedServices[s.id])
    .reduce((sum, s) => sum + s.price, 0);
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Wrench className="w-5 h-5 text-indigo-600" />
          Installation & Setup Services
        </CardTitle>
        <CardDescription>
          Optional professional services to help you get started
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {installationServices.map(service => (
          <div 
            key={service.id}
            className={`p-4 rounded-lg border cursor-pointer transition-all ${
              selectedServices[service.id] ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => handleToggle(service.id)}
          >
            <div className="flex items-start gap-3">
              <Checkbox checked={selectedServices[service.id] || false} className="mt-1" />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium">{service.name}</p>
                  {service.recommended && (
                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700">Recommended</Badge>
                  )}
                  {service.safety && (
                    <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700">
                      <Shield className="w-3 h-3 mr-1" /> Info-only
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-gray-500">{service.description}</p>
              </div>
              <span className="font-medium">
                {listing?.currency === 'LKR' || (listing?.country || '').toUpperCase() === 'LK' || (listing?.country || '').toUpperCase() === 'SL' ? 'Rs.' : '£'}{service.price}
              </span>
            </div>
          </div>
        ))}
        
        {/* Safety disclaimer */}
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
            <p className="text-xs text-amber-700">
              Installation assistance is guidance only and does not constitute official certification or safety approval.
            </p>
          </div>
        </div>
        
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Services total</span>
            <span className="text-lg font-bold">
              {listing?.currency === 'LKR' || (listing?.country || '').toUpperCase() === 'LK' || (listing?.country || '').toUpperCase() === 'SL' ? 'Rs.' : '£'}{totalServicePrice}
            </span>
          </div>
        </div>
        
        <div className="flex gap-3">
          <Button variant="outline" onClick={onBack}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button 
            className="flex-1" 
            onClick={() => onNext({ services: selectedServices, price: totalServicePrice })}
          >
            Continue to Schedule
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Schedule Step
function ScheduleStep({ listing, schedule, onScheduleChange, onNext, onBack }) {
  const [date, setDate] = useState(null);
  const [timeSlot, setTimeSlot] = useState('');
  const [notes, setNotes] = useState('');
  
  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 2); // At least 2 days from now
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarIcon className="w-5 h-5 text-indigo-600" />
          Schedule Your Appointment
        </CardTitle>
        <CardDescription>
          Choose a convenient date and time for delivery/service
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Calendar */}
          <div>
            <Label className="mb-2 block">Select Date</Label>
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={(d) => d < minDate}
              className="rounded-md border"
            />
          </div>
          
          {/* Time slots */}
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">Preferred Time</Label>
              <RadioGroup value={timeSlot} onValueChange={setTimeSlot}>
                {TIME_SLOTS.map(slot => (
                  <label 
                    key={slot.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${
                      timeSlot === slot.id ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'
                    }`}
                  >
                    <RadioGroupItem value={slot.id} />
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-gray-500" />
                      <span>{slot.label}</span>
                    </div>
                  </label>
                ))}
              </RadioGroup>
            </div>
            
            <div>
              <Label className="mb-2 block">Special Instructions (optional)</Label>
              <Textarea 
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any special instructions for the delivery or service team..."
                rows={3}
              />
            </div>
          </div>
        </div>
        
        {date && timeSlot && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2 text-green-800">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">
                Scheduled for {date.toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </span>
            </div>
            <p className="text-sm text-green-700 mt-1">
              {TIME_SLOTS.find(s => s.id === timeSlot)?.label}
            </p>
          </div>
        )}
        
        <div className="flex gap-3">
          <Button variant="outline" onClick={onBack}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button 
            className="flex-1" 
            onClick={() => onNext({ date, timeSlot, notes })}
            disabled={!date || !timeSlot}
          >
            Continue to Payment
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Payment Step
function PaymentStep({ 
  listing, 
  bookingData,
  onConfirm, 
  onBack,
  loading 
}) {
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState(bookingData.delivery?.phone || '');
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [agreeTerms, setAgreeTerms] = useState(false);
  
  const currencySymbol = listing?.currency === 'LKR' || (listing?.country || '').toUpperCase() === 'LK' || (listing?.country || '').toUpperCase() === 'SL' ? 'Rs.' : '£';
  
  // Calculate total
  const basePrice = listing?.price || 0;
  const deliveryPrice = bookingData.delivery?.price || 0;
  const servicesPrice = bookingData.services?.price || 0;
  const totalPrice = basePrice + deliveryPrice + servicesPrice;
  
  const handleSubmit = () => {
    if (!customerName || !customerEmail || !agreeTerms) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    onConfirm({
      customerName,
      customerEmail,
      customerPhone,
      paymentMethod,
      totalPrice
    });
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-indigo-600" />
          Review & Pay
        </CardTitle>
        <CardDescription>
          Confirm your booking details and complete payment
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Order Summary */}
        <div className="p-4 bg-gray-50 rounded-lg space-y-3">
          <h4 className="font-semibold">Order Summary</h4>
          
          <div className="flex justify-between text-sm">
            <span>{listing?.title}</span>
            <span>{currencySymbol}{basePrice}</span>
          </div>
          
          {deliveryPrice > 0 && (
            <div className="flex justify-between text-sm">
              <span>Delivery ({bookingData.delivery?.option})</span>
              <span>{currencySymbol}{deliveryPrice}</span>
            </div>
          )}
          
          {servicesPrice > 0 && (
            <div className="flex justify-between text-sm">
              <span>Installation Services</span>
              <span>{currencySymbol}{servicesPrice}</span>
            </div>
          )}
          
          <div className="border-t pt-2 flex justify-between font-bold">
            <span>Total</span>
            <span className="text-lg text-indigo-600">{currencySymbol}{totalPrice.toFixed(2)}</span>
          </div>
        </div>
        
        {/* Customer Details */}
        <div className="space-y-4">
          <h4 className="font-semibold">Your Details</h4>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Full Name *</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input 
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="John Doe"
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <Label>Email *</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input 
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="john@example.com"
                  className="pl-10"
                />
              </div>
            </div>
          </div>
          <div>
            <Label>Phone</Label>
            <div className="relative mt-1">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input 
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                placeholder="+44 7XXX XXX XXX"
                className="pl-10"
              />
            </div>
          </div>
        </div>
        
        {/* Payment Method */}
        <div className="space-y-3">
          <h4 className="font-semibold">Payment Method</h4>
          <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
            <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${
              paymentMethod === 'card' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'
            }`}>
              <RadioGroupItem value="card" />
              <CreditCard className="w-5 h-5" />
              <span>Pay by Card</span>
            </label>
            {listing?.currency === 'LKR' && (
              <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${
                paymentMethod === 'cod' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'
              }`}>
                <RadioGroupItem value="cod" />
                <Package className="w-5 h-5" />
                <span>Cash on Delivery</span>
              </label>
            )}
          </RadioGroup>
        </div>
        
        {/* Terms */}
        <div className="flex items-start gap-2">
          <Checkbox 
            id="terms" 
            checked={agreeTerms}
            onCheckedChange={setAgreeTerms}
          />
          <label htmlFor="terms" className="text-sm text-gray-600">
            I agree to the Terms of Service and acknowledge that installation assistance is guidance only, not official certification.
          </label>
        </div>
        
        <div className="flex gap-3">
          <Button variant="outline" onClick={onBack}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button 
            className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600"
            onClick={handleSubmit}
            disabled={loading || !customerName || !customerEmail || !agreeTerms}
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CreditCard className="w-4 h-4 mr-2" />
            )}
            {paymentMethod === 'cod' ? 'Confirm Order' : `Pay ${currencySymbol}${totalPrice.toFixed(2)}`}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Confirmation Step
function ConfirmationStep({ bookingId, listing, bookingData }) {
  const navigate = useNavigate();
  const currencySymbol = listing?.currency === 'LKR' || (listing?.country || '').toUpperCase() === 'LK' || (listing?.country || '').toUpperCase() === 'SL' ? 'Rs.' : '£';
  
  return (
    <Card className="max-w-lg mx-auto">
      <CardContent className="py-8 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Booking Confirmed!</h2>
        <p className="text-gray-500 mb-6">
          Your booking has been successfully placed.
        </p>
        
        <div className="p-4 bg-gray-50 rounded-lg text-left mb-6">
          <p className="text-sm text-gray-500">Booking Reference</p>
          <p className="text-lg font-mono font-bold text-indigo-600">{bookingId}</p>
          
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Service</span>
              <span>{listing?.title}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Date</span>
              <span>{bookingData.schedule?.date?.toLocaleDateString('en-GB')}</span>
            </div>
            <div className="flex justify-between font-bold">
              <span>Total Paid</span>
              <span className="text-indigo-600">{currencySymbol}{bookingData.payment?.totalPrice}</span>
            </div>
          </div>
        </div>
        
        <p className="text-sm text-gray-500 mb-6">
          A confirmation email has been sent to {bookingData.payment?.customerEmail}
        </p>
        
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            className="flex-1"
            onClick={() => navigate('/category/baby-kids-items')}
          >
            Continue Shopping
          </Button>
          <Button 
            className="flex-1"
            onClick={() => navigate('/my-bookings')}
          >
            View Bookings
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Main Component
export default function BabyKidsBookingFlow() {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [step, setStep] = useState(1);
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bookingId, setBookingId] = useState(null);
  
  // Booking data state
  const [bookingData, setBookingData] = useState({
    bundle: {},
    delivery: null,
    services: null,
    schedule: null,
    payment: null
  });
  
  // Sample bundle add-ons
  const bundleItems = useMemo(() => [
    { id: 'gift_wrap', name: 'Gift Wrapping', price: listing?.currency === 'LKR' ? 500 : 5, recommended: false },
    { id: 'express', name: 'Priority Processing', price: listing?.currency === 'LKR' ? 1000 : 10, recommended: false },
    { id: 'insurance', name: 'Damage Protection', price: listing?.currency === 'LKR' ? 800 : 8, recommended: true },
  ], [listing?.currency]);
  
  // Fetch listing
  useEffect(() => {
    const fetchListing = async () => {
      if (!listingId) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await fetch(`${API_URL}/api/baby-kids/listings/${listingId}`);
        const data = await response.json();
        
        if (data.success) {
          setListing(data.listing);
        }
      } catch (error) {
        console.error('Failed to fetch listing:', error);
        toast.error('Failed to load listing');
      } finally {
        setLoading(false);
      }
    };
    
    fetchListing();
  }, [listingId]);
  
  // Handle booking submission
  const handleConfirmBooking = async (paymentData) => {
    setSubmitting(true);
    
    try {
      const bookingPayload = {
        listing_id: listingId,
        customer_name: paymentData.customerName,
        customer_email: paymentData.customerEmail,
        customer_phone: paymentData.customerPhone,
        delivery_option: bookingData.delivery?.option,
        delivery_address: bookingData.delivery?.address,
        delivery_postcode: bookingData.delivery?.postcode,
        services: bookingData.services?.services || {},
        schedule_date: bookingData.schedule?.date?.toISOString(),
        schedule_time: bookingData.schedule?.timeSlot,
        notes: bookingData.schedule?.notes,
        payment_method: paymentData.paymentMethod,
        total_price: paymentData.totalPrice,
      };
      
      // For hire bookings
      if (listing?.card_type === 'hire' || listing?.card_type === 'subscription') {
        const response = await fetch(`${API_URL}/api/baby-kids/hire/book`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customer_name: paymentData.customerName,
            customer_email: paymentData.customerEmail,
            customer_phone: paymentData.customerPhone,
            listing_id: listingId,
            hire_type: listing?.card_type === 'subscription' ? 'subscription' : 'weekly',
            start_date: bookingData.schedule?.date?.toISOString().split('T')[0],
            duration_weeks: 1,
            notes: bookingData.schedule?.notes,
          }),
        });
        
        const data = await response.json();
        
        if (data.success) {
          setBookingId(data.booking_id);
          setBookingData(prev => ({ ...prev, payment: paymentData }));
          setStep(6); // Confirmation
          toast.success('Booking confirmed!');
        } else {
          toast.error(data.detail || 'Booking failed');
        }
      } else if (listing?.card_type === 'party' || listing?.card_type === 'venue') {
        // Party booking
        const response = await fetch(`${API_URL}/api/baby-kids/party/book`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customer_name: paymentData.customerName,
            customer_email: paymentData.customerEmail,
            customer_phone: paymentData.customerPhone,
            listing_id: listingId,
            event_date: bookingData.schedule?.date?.toISOString().split('T')[0],
            event_time: bookingData.schedule?.timeSlot,
            event_duration: 2,
            venue_address: bookingData.delivery?.address,
            children_count: 10,
            age_range: '3-10',
            special_requests: bookingData.schedule?.notes,
          }),
        });
        
        const data = await response.json();
        
        if (data.success) {
          setBookingId(data.booking_id);
          setBookingData(prev => ({ ...prev, payment: paymentData }));
          setStep(6);
          toast.success('Party booking confirmed!');
        } else {
          toast.error(data.detail || 'Booking failed');
        }
      } else {
        // Generic booking - simulate success
        setBookingId(`BK-${Date.now()}`);
        setBookingData(prev => ({ ...prev, payment: paymentData }));
        setStep(6);
        toast.success('Booking confirmed!');
      }
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Failed to complete booking');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }
  
  if (!listing && !listingId) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h1 className="text-2xl font-bold mb-4">No Service Selected</h1>
          <p className="text-gray-500 mb-6">Please select a service from the Baby & Kids category.</p>
          <Button onClick={() => navigate('/category/baby-kids-items')}>
            Browse Services
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        {step < 6 && (
          <>
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-gray-900">Complete Your Booking</h1>
              <p className="text-gray-500">{listing?.title}</p>
            </div>
            
            {/* Progress */}
            <StepProgress currentStep={step} steps={STEPS} />
          </>
        )}
        
        {/* Steps */}
        {step === 1 && (
          <BundleStep 
            listing={listing}
            bundleItems={bundleItems}
            onItemToggle={(items) => setBookingData(prev => ({ ...prev, bundle: items }))}
            onNext={(items) => {
              setBookingData(prev => ({ ...prev, bundle: items }));
              setStep(2);
            }}
          />
        )}
        
        {step === 2 && (
          <DeliveryStep 
            listing={listing}
            deliveryOption={bookingData.delivery?.option}
            onOptionChange={(opt) => setBookingData(prev => ({ ...prev, delivery: { ...prev.delivery, option: opt }}))}
            onNext={(delivery) => {
              setBookingData(prev => ({ ...prev, delivery }));
              setStep(3);
            }}
            onBack={() => setStep(1)}
          />
        )}
        
        {step === 3 && (
          <ServicesStep 
            listing={listing}
            services={bookingData.services}
            onServicesChange={(services) => setBookingData(prev => ({ ...prev, services: { ...prev.services, services }}))}
            onNext={(services) => {
              setBookingData(prev => ({ ...prev, services }));
              setStep(4);
            }}
            onBack={() => setStep(2)}
          />
        )}
        
        {step === 4 && (
          <ScheduleStep 
            listing={listing}
            schedule={bookingData.schedule}
            onScheduleChange={(schedule) => setBookingData(prev => ({ ...prev, schedule }))}
            onNext={(schedule) => {
              setBookingData(prev => ({ ...prev, schedule }));
              setStep(5);
            }}
            onBack={() => setStep(3)}
          />
        )}
        
        {step === 5 && (
          <PaymentStep 
            listing={listing}
            bookingData={bookingData}
            onConfirm={handleConfirmBooking}
            onBack={() => setStep(4)}
            loading={submitting}
          />
        )}
        
        {step === 6 && (
          <ConfirmationStep 
            bookingId={bookingId}
            listing={listing}
            bookingData={bookingData}
          />
        )}
      </div>
    </div>
  );
}
